import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, Target, Zap, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';

const TypesParisSportifs = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center text-toxic-green hover:text-opacity-80 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux guides
          </Link>

          <article className="bg-dark-card border-2 border-gray-800 rounded-2xl p-8 md:p-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Les Types de Paris Sportifs
            </h1>

            <div className="prose prose-invert max-w-none">
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Le monde des paris sportifs offre une variété impressionnante d'options. Du pari simple au combiné complexe, chaque type de pari présente ses avantages, ses risques et ses stratégies spécifiques. Maîtriser ces différents types est essentiel pour développer une approche cohérente et profitable.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Le Pari Simple : La Base de Tout
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Le pari simple est le plus basique et le plus populaire. Vous misez sur un seul résultat : l'équipe qui va gagner, le nombre de buts, le score exact. C'est simple, direct, et les chances de gagner sont généralement plus élevées que pour les paris combinés. Pour les débutants, c'est le point de départ idéal.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les cotes pour les paris simples sont généralement plus basses que pour les combinés, mais vos chances de gagner sont proportionnellement plus élevées. Si vous pariez 10€ sur une équipe favorite à 1.5, vous gagnez 15€ (5€ de profit). C'est modeste, mais c'est sûr. Pour les parieurs conservateurs, c'est la stratégie de choix.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les variantes du pari simple incluent le 1X2 (victoire domicile, match nul, victoire extérieur), le double chance (vous couvrez deux résultats sur trois), et les paris sur le nombre de buts (plus ou moins de 2.5 buts par exemple). Chaque variante ajuste le niveau de risque et de récompense.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Paris Combinés : Multiplier les Gains
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Un pari combiné (ou accumulateur) combine plusieurs sélections en un seul pari. Toutes les sélections doivent être correctes pour que le pari soit gagnant. Si une seule échoue, tout le pari est perdu. C'est risqué, mais les gains potentiels sont considérablement plus élevés.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Par exemple, si vous combinez trois paris à 2.0 chacun, la cote combinée est de 8.0 (2 x 2 x 2). Votre mise de 10€ peut rapporter 80€. Mais si l'un des trois paris échoue, vous perdez tout. C'est pourquoi les paris combinés sont souvent réservés aux parieurs expérimentés qui comprennent bien les risques.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les stratégies pour les combinés incluent de limiter le nombre de sélections (2 à 4 paris maximum), de choisir des événements avec des cotes modérées plutôt que très élevées, et de ne jamais inclure un pari "coup de cœur" risqué dans un combiné important.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Le Live Betting : L'Action en Temps Réel
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Le live betting (paris en direct) vous permet de parier pendant que l'événement se déroule. Les cotes évoluent en temps réel selon l'action sur le terrain. C'est excitant, mais cela nécessite une bonne compréhension du sport et une capacité à prendre des décisions rapides.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les avantages du live betting incluent la possibilité de voir comment l'événement se déroule avant de parier, et des cotes qui peuvent devenir très favorables si la situation change. Par exemple, si une équipe mène 2-0, les cotes pour qu'elle gagne baissent, mais les cotes pour un retournement de situation augmentent.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Le risque principal est l'impulsivité. Il est facile de se laisser emporter par l'action et de faire des paris que vous n'auriez pas faits avant le match. Fixez-vous des limites avant de commencer, et respectez-les.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Paris sur les Statistiques
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Au-delà du résultat final, vous pouvez parier sur presque toutes les statistiques d'un match : nombre de corners, cartons jaunes, fautes, possession de balle, tirs au but. Ces paris sont populaires car ils ne dépendent pas uniquement du résultat, ce qui peut offrir de meilleures valeurs.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Pour réussir avec les paris statistiques, vous devez bien connaître les équipes et leurs styles de jeu. Une équipe qui joue de manière agressive aura tendance à commettre plus de fautes et à recevoir plus de cartons. Une équipe qui domine aura plus de corners. Analysez les statistiques passées pour identifier les tendances.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Paris Système : Réduire les Risques
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris système vous permettent de combiner plusieurs sélections de manière à ce que vous puissiez gagner même si certaines échouent. Par exemple, un système 2/3 signifie que vous faites tous les paris combinés possibles avec 2 sélections sur vos 3 choix. Si 2 sur 3 sont corrects, vous gagnez au moins un pari.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                C'est plus sûr qu'un combiné classique, mais cela nécessite une mise plus importante car vous faites plusieurs paris. Si vous avez 3 sélections et que vous faites un système 2/3, vous faites 3 paris combinés (A+B, A+C, B+C). Votre mise totale est donc multipliée par 3.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Choisir le Bon Type de Pari
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Le choix du type de pari dépend de votre objectif, de votre budget et de votre niveau de confiance. Si vous débutez, restez sur les paris simples. Si vous avez un budget limité, évitez les combinés à nombreuses sélections. Si vous êtes confiant sur plusieurs événements, un combiné modéré (2-3 sélections) peut être rentable.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                N'oubliez jamais que chaque type de pari a sa place dans une stratégie équilibrée. Les paris simples pour la stabilité, les combinés pour les gains importants, le live betting pour l'action, les statistiques pour la valeur. Variez vos approches selon les situations.
              </p>

              <div className="bg-toxic-green/10 border border-toxic-green/30 rounded-xl p-6 mt-12">
                <h3 className="text-2xl font-bold mb-4 text-white flex items-center">
                  <Target className="w-6 h-6 text-toxic-green mr-3" />
                  Points à Retenir
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Commencez par les paris simples avant de vous aventurer dans les combinés</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Limitez le nombre de sélections dans vos combinés pour maintenir des chances raisonnables</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Le live betting nécessite discipline et connaissance du sport</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Les paris statistiques peuvent offrir de meilleures valeurs si vous connaissez bien les équipes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Variez vos types de paris selon les situations et votre niveau de confiance</span>
                  </li>
                </ul>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TypesParisSportifs;

